import { UtilInfo } from 'features/utils/api/models/UtilInfo';
import React, { createContext, useState, useContext, useCallback, ReactNode } from 'react';

interface UtilsDialogContextType {
  isDialogOpen: boolean;
  editingUtil: UtilInfo | null;
  openAddDialog: () => void;
  openEditDialog: (util: UtilInfo) => void;
  closeDialog: () => void;
}

const UtilsDialogContext = createContext<UtilsDialogContextType | undefined>(undefined);

interface UtilsDialogProviderProps {
  children: ReactNode;
}

export const UtilsDialogProvider: React.FC<UtilsDialogProviderProps> = ({ children }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUtil, setEditingUtil] = useState<UtilInfo | null>(null);

  const openAddDialog = useCallback(() => {
    setEditingUtil(null);
    setIsDialogOpen(true);
  }, []);

  const openEditDialog = useCallback((util: UtilInfo) => {
    setEditingUtil(util);
    setIsDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
    setEditingUtil(null);
  }, []);

  const value = {
    isDialogOpen,
    editingUtil,
    openAddDialog,
    openEditDialog,
    closeDialog,
  };

  return <UtilsDialogContext.Provider value={value}>{children}</UtilsDialogContext.Provider>;
};

export const useUtilsDialog = (): UtilsDialogContextType => {
  const context = useContext(UtilsDialogContext);
  if (context === undefined) {
    throw new Error('useUtilsDialog must be used within a UtilsDialogProvider');
  }
  return context;
};
